import React, { useEffect, useRef, useState } from 'react';
import './bar.scss';
import { formatTime, getPosPercent, getTimeScale } from './util';
type ControlerBarProps = {
  duration: number;
  currentTime: number;
  handleEventPlay: AnyFunc;
  handleEventPause: AnyFunc;
  handleControlChange: AnyFunc;
  isPaused: Boolean;
  extraBtn: React.ReactNode;
};
const ControlerBar = (props: ControlerBarProps) => {
  const {
    currentTime,
    duration,
    handleEventPlay,
    handleEventPause,
    isPaused,
    handleControlChange,
    extraBtn,
  } = props;

  const trackElement = useRef<HTMLDivElement>(null);
  const hoverTimeElement = useRef<HTMLDivElement>(null);
  const seeking = useRef(false);
  const [seekHoverPosition, setSeekHoverPos] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const hoverTimeClassName =
    seekHoverPosition > 0 || seeking.current
      ? 'hover-time  active'
      : 'hover-time';
  const [pageX, setPageX] = useState(0); // 鼠标点击的水平位置
  const setTrackWidthState = () => {
    if (trackElement.current) {
      setTrackWidth(trackElement.current.offsetWidth);
    }
  };
  // 处理进度条跳转
  useEffect(() => {
    if (seeking.current) {
      changeCurrentTimePosition(pageX);
    }
  }, [pageX]);

  // 绑定事件
  useEffect(() => {
    setTrackWidthState();

    window.addEventListener('resize', setTrackWidthState);
    window.addEventListener('mouseup', mouseSeekingHandler);

    return () => {
      window.removeEventListener('resize', setTrackWidthState);
      window.removeEventListener('mouseup', mouseSeekingHandler);
    };
  }, [duration, trackWidth]);

  const posPercent = getPosPercent(duration, 0); // 初始位置为0
  const translateX = (trackWidth / 100) * posPercent;

  const currentTimeScale = getTimeScale(currentTime, 0, duration);

  const seekHoverTimeScale = getTimeScale(
    Math.floor(+(((seekHoverPosition * 100) / trackWidth) * (duration / 100))),
    0,
    duration,
  );

  // 获取鼠标hover位置的时间
  const getHoverTime = (
    seekHoverPosition: number,
    trackWidth: number,
    duration: number,
  ) => {
    const percent = (seekHoverPosition * 100) / trackWidth;
    const hoverTime = formatTime(Math.floor(+(percent * (duration / 100))));
    return hoverTime;
  };
  const hoverTimeStr = getHoverTime(seekHoverPosition, trackWidth, duration);

  // 获取鼠标hover的位置
  const getHoverTimePosition = (
    seekHoverPosition: number,
    hoverTimeElement: HTMLDivElement | null,
    trackWidth: number,
  ): { transform: string } => {
    let position = 0;

    if (hoverTimeElement) {
      position = seekHoverPosition - hoverTimeElement.offsetWidth / 2;

      if (position < 0) {
        position = 0;
      } else if (position + hoverTimeElement.offsetWidth > trackWidth) {
        position = trackWidth - hoverTimeElement.offsetWidth;
      }
    }

    return { transform: `translateX(${position}px)` };
  };
  const hoverTimePos = getHoverTimePosition(
    seekHoverPosition,
    hoverTimeElement?.current,
    trackWidth,
  );

  // 改变进度条当前播放时间
  const changeCurrentTimePosition = (pageX: number) => {
    const left = trackElement.current?.getBoundingClientRect().left || 0;
    let position = pageX - left;

    position = position < 0 ? 0 : position;
    position = position > trackWidth ? trackWidth : position;

    setSeekHoverPos(position);

    const percent = (position * 100) / trackWidth;
    const time = +(percent * (duration / 100)).toFixed(0);

    handleControlChange(time);
  };

  const handleTrackHover = (
    clear: boolean,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const left = trackElement.current?.getBoundingClientRect().left || 0;
    const position = clear ? 0 : event.pageX - left;

    setSeekHoverPos(position);
  };

  const getThumbHandlerPosition = () => {
    const position = trackWidth / (duration / currentTime);

    return { transform: `translateX(${position}px)` };
  };

  const setSeeking = (state: boolean, event: MouseEvent) => {
    event.preventDefault();
    setPageX(event.pageX);
    seeking.current = state;
    setSeekHoverPos(state ? seekHoverPosition : 0);
  };

  const mouseSeekingHandler = (event: MouseEvent) => {
    setSeeking(false, event);
  };

  return (
    <div className='custorm-control_bar'>
      <div className='control_bar'>
        {!isPaused ? (
          <i className='stop-btn' onClick={handleEventPause}></i>
        ) : (
          <i className='play-btn' onClick={handleEventPlay}></i>
        )}
        <span className='play-time'>
          {formatTime(currentTime)} /{' '}
          {duration !== 0 ? formatTime(duration) : '00:00:00'}
        </span>
        <div className='progress-bar'>
          <div
            className='track'
            ref={trackElement}
            onMouseMove={(event) => handleTrackHover(false, event)}
            onMouseLeave={(event) => handleTrackHover(true, event)}
            onMouseDown={(event) => setSeeking(true, event as any)}
          >
            <div
              className='main'
              style={{
                width: `${trackWidth}px`,
                left: `${translateX}px`,
              }}
            >
              <div
                className='inner-seek-block seek-hover'
                style={{ transform: `scaleX(${seekHoverTimeScale})` }}
              />

              <div
                className='inner-seek-block connect'
                style={{ transform: `scaleX(${currentTimeScale})` }}
              />
            </div>
          </div>

          <div
            className={hoverTimeClassName}
            style={hoverTimePos}
            ref={hoverTimeElement}
          >
            {hoverTimeStr}
          </div>

          <div className='thumb active' style={getThumbHandlerPosition()}>
            <div className='handler' />
          </div>
        </div>
        <div className='control-btn-group'>{extraBtn ?? <></>}</div>
      </div>
    </div>
  );
};

export default React.memo(ControlerBar);
